import React, { useState } from 'react';
import Avatar from '@/components/Avatar';
import StatusPill from '@/components/StatusPill';
import { I } from '@/lib/icons';
import { fmtEur, fmtDate } from '@/lib/format';

export default function InvoiceRow({ invoice, divider, onClick, onMarkPaid, workspaceLabel }) {
  const client = invoice.client;
  const [markingPaid, setMarkingPaid] = useState(false);

  const showMarkPaid = !!onMarkPaid && invoice.status !== 'paid' && invoice.status !== 'cancelled';

  const handleMarkPaid = async (e) => {
    e.stopPropagation();
    if (!onMarkPaid || markingPaid) return;
    setMarkingPaid(true);
    try {
      await onMarkPaid(invoice);
    } finally {
      setMarkingPaid(false);
    }
  };

  return (
    <div onClick={onClick} data-testid={`invoice-row-${invoice.id}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
        borderBottom: divider ? '1px solid var(--border)' : 'none',
        cursor: 'pointer',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <Avatar name={client?.name || '?'} size={36} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>{client?.name || 'Bez klienta'}</span>
          {client?.is_pinned && <span style={{ color: 'var(--text-muted)' }}><I.pin /></span>}
          {workspaceLabel && (
            <span style={{
              fontSize: 10, color: 'var(--text-muted)', background: 'var(--bg-hover)',
              padding: '2px 6px', borderRadius: 'var(--r-sm)', marginLeft: 4,
            }}>{workspaceLabel}</span>
          )}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          {invoice.number} · VS {invoice.variable_symbol || invoice.number}
          {invoice.issue_date && ` · vyst. ${fmtDate(invoice.issue_date)}`}
          {invoice.due_date && ` · spl. ${fmtDate(invoice.due_date)}`}
        </div>
      </div>
      <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div>
          <div style={{
            fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '-0.01em',
          }}>{fmtEur(invoice.total)}</div>
          <div style={{ marginTop: 4 }}><StatusPill status={invoice.status} invoice={invoice} /></div>
        </div>
        {showMarkPaid && (
          <button
            type="button"
            onClick={handleMarkPaid}
            disabled={markingPaid}
            data-testid={`invoice-mark-paid-${invoice.id}`}
            aria-label="Označiť ako zaplatenú"
            style={{
              fontSize: 11.5, padding: '5px 10px', borderRadius: 'var(--r-full)',
              background: 'var(--sec-green-soft)', color: 'var(--sec-green)',
              border: '1px solid transparent', cursor: markingPaid ? 'wait' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontFamily: 'inherit', fontWeight: 600,
              transition: 'background 0.15s var(--ease), color 0.15s var(--ease)',
            }}
            onMouseEnter={e => {
              if (!markingPaid) {
                e.currentTarget.style.background = 'var(--sec-green)';
                e.currentTarget.style.color = '#fff';
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'var(--sec-green-soft)';
              e.currentTarget.style.color = 'var(--sec-green)';
            }}
          >
            {markingPaid
              ? <span className="cd-spinner" style={{ borderTopColor: 'currentColor' }} />
              : <I.check width={14} height={14} />}
            <span>Zaplatené</span>
          </button>
        )}
      </div>
    </div>
  );
}
